import React, { useEffect, useMemo, useState } from "react";
import {
  buildTonelagHeaders,
  normalizeTonelagApiBase,
  TONELAG_API_BASE_URL,
} from "./tonelagApiFinal";

/**
 * TonelagGroupsExplorer
 *
 * Research-oriented interface for checking Martin Kloster Jensen's
 * "Bokmålets tonelagspar («Vippere»)" against the printed book.
 *
 * Required data:
 *   data.classification.ending_classes
 *   data.classification.groups
 *   data.pair_candidates
 *
 * Optional props:
 *   manualCorrections     Object keyed by record ID
 *   scanImageBasePath     Example: "/book_tonelag"
 *                         Resolves viewer page 17 as
 *                         /book_tonelag/page_0017.jpg
 *   getScanImageUrl       Custom function: pair => string | null
 *   getSourcePageUrl      Custom function: pair => string | null
 *   onReviewChange        Called after verify/correct/flag/reject actions
 *   initialMode           "browse" | "review"
 *
 * No third-party UI package is required. Tailwind CSS is expected.
 */

const DEFAULT_MANUAL_CORRECTIONS = {
  "g01-0014": {
    accent_2_form: "dingle",
    verified_against_scan: true,
    note: "Corrected from OCR «diugle» after checking printed page 15.",
    frequency_is_stale: true,
  },
};

const QUALITY_STYLES = {
  blue: {
    label: "AI-selected form",
    shortLabel: "AI selected",
    dot: "bg-sky-500",
    border: "border-l-sky-500",
    row: "bg-sky-50/30 hover:bg-sky-50/70",
    badge: "border-sky-200 bg-sky-50 text-sky-900",
    bar: "bg-sky-500",
  },
  green: {
    label: "High confidence",
    shortLabel: "High",
    dot: "bg-emerald-500",
    border: "border-l-emerald-500",
    row: "hover:bg-emerald-50/60",
    badge: "border-emerald-200 bg-emerald-50 text-emerald-800",
    bar: "bg-emerald-500",
  },
  yellow: {
    label: "Needs review",
    shortLabel: "Review",
    dot: "bg-amber-400",
    border: "border-l-amber-400",
    row: "bg-amber-50/35 hover:bg-amber-50",
    badge: "border-amber-200 bg-amber-50 text-amber-900",
    bar: "bg-amber-400",
  },
  red: {
    label: "Low confidence",
    shortLabel: "Low",
    dot: "bg-rose-500",
    border: "border-l-rose-500",
    row: "bg-rose-50/40 hover:bg-rose-50",
    badge: "border-rose-200 bg-rose-50 text-rose-800",
    bar: "bg-rose-500",
  },
};

const ENDING_CLASS_LABELS = {
  final_schwa: "Utlyd -ə",
  final_er: "Utlyd -er",
  final_en: "Utlyd -en",
  other_endings: "Andre utlydsformer",
};

const FREQUENCY_TIER_LABELS = {
  very_common: "Very common",
  common: "Common",
  moderate: "Moderate",
  uncommon: "Uncommon",
  rare: "Rare",
  very_rare: "Very rare",
  unattested: "Unattested",
  not_measurable: "Not measurable",
};

const FREQUENCY_MODE_LABELS = {
  available: "Available anywhere",
  modern: "Modern use",
  historical: "Historical use",
};

const MULTICORPUS_TIER_LABELS = {
  ...FREQUENCY_TIER_LABELS,
  attested_many: "Widely attested",
  attested_frequent: "Frequently attested",
  attested: "Attested",
  attested_rare: "Rarely attested",
  single_match: "Single match",
  pending: "Pending",
};

const TIER_BADGE_STYLES = {
  very_common:
    "border-emerald-700 bg-emerald-600 text-white shadow-sm",
  common:
    "border-green-500 bg-green-100 text-green-950",
  attested_many:
    "border-green-500 bg-green-100 text-green-950",
  moderate:
    "border-sky-500 bg-sky-100 text-sky-950",
  attested_frequent:
    "border-sky-500 bg-sky-100 text-sky-950",
  uncommon:
    "border-violet-500 bg-violet-100 text-violet-950",
  attested:
    "border-violet-500 bg-violet-100 text-violet-950",
  rare:
    "border-amber-500 bg-amber-100 text-amber-950",
  attested_rare:
    "border-amber-500 bg-amber-100 text-amber-950",
  very_rare:
    "border-rose-800 bg-rose-600 text-white shadow-sm",
  single_match:
    "border-rose-800 bg-rose-600 text-white shadow-sm",
  unattested:
    "border-slate-700 bg-slate-700 text-white",
  not_measurable:
    "border-slate-400 bg-slate-100 text-slate-800",
  pending:
    "border-sky-500 bg-sky-100 text-sky-950",
};

const TIER_BADGE_MARKERS = {
  very_common: "▲",
  common: "●",
  attested_many: "●",
  moderate: "●",
  attested_frequent: "●",
  uncommon: "●",
  attested: "●",
  rare: "◆",
  attested_rare: "◆",
  very_rare: "▼",
  single_match: "▼",
  unattested: "×",
  not_measurable: "—",
  pending: "…",
};

function normalizedTierKey(tier) {
  return String(tier ?? "")
    .trim()
    .toLocaleLowerCase("en-US")
    .replace(/[\s-]+/g, "_");
}

function tierBadgeStyle(tier) {
  return (
    TIER_BADGE_STYLES[normalizedTierKey(tier)] ??
    "border-slate-300 bg-white text-slate-800"
  );
}

function tierBadgeMarker(tier) {
  return TIER_BADGE_MARKERS[normalizedTierKey(tier)] ?? "●";
}

const FREQUENCY_TIER_RARITY_ORDER = {
  very_common: 0,
  common: 1,
  attested_many: 1,
  moderate: 2,
  attested_frequent: 2,
  uncommon: 3,
  attested: 3,
  rare: 4,
  attested_rare: 4,
  very_rare: 5,
  single_match: 5,
  unattested: 6,
  not_measurable: 7,
  pending: 8,
};

function frequencyTierLabel(tier) {
  return MULTICORPUS_TIER_LABELS[tier] ?? tier ?? null;
}

function rarerFrequencyTier(leftTier, rightTier) {
  const leftRank =
    FREQUENCY_TIER_RARITY_ORDER[leftTier] ?? Number.POSITIVE_INFINITY;
  const rightRank =
    FREQUENCY_TIER_RARITY_ORDER[rightTier] ?? Number.POSITIVE_INFINITY;

  if (!leftTier) return rightTier ?? null;
  if (!rightTier) return leftTier ?? null;

  return leftRank >= rightRank ? leftTier : rightTier;
}

function attestedSideTier(pairProfile) {
  if (!pairProfile) return null;

  const leftAttested = pairProfile.left_status === "attested";
  const rightAttested = pairProfile.right_status === "attested";

  if (leftAttested && rightAttested) {
    return rarerFrequencyTier(
      pairProfile.left_tier,
      pairProfile.right_tier
    );
  }

  if (leftAttested) return pairProfile.left_tier ?? null;
  if (rightAttested) return pairProfile.right_tier ?? null;

  return null;
}

function pairFrequencyTier(pair, frequencyMode = "available") {
  const pairProfile =
    pair?.multi_corpus_frequency?.pair_profiles?.[frequencyMode];

  const profileTier = attestedSideTier(pairProfile);
  if (profileTier) return profileTier;

  // Backward-compatible fallback for JSON without multi-corpus profiles.
  const oldPair = pair?.frequency_evidence?.pair;
  if (oldPair?.written_forms_identical_casefold) {
    return oldPair.shared_surface_frequency_tier ?? null;
  }

  return oldPair?.pedagogical_surface_frequency_tier ?? null;
}

const DISAMBIGUATION_LABELS = {
  different_spelling_but_lexical_disambiguation_may_still_be_needed:
    "Different spellings; POS or lexical verification may still be needed.",
  identical_spelling_pos_or_morphology_may_separate:
    "Identical spelling; POS or morphology may separate the members.",
  identical_spelling_semantic_disambiguation_required:
    "Identical spelling; semantic disambiguation is required.",
  surface_form_not_cleanly_measurable:
    "At least one OCR form cannot be treated as a clean written token.",
};

const REVIEW_STATUS_LABELS = {
  verified: "Verified against scan",
  corrected: "Corrected; verify once more",
  needs_correction: "Flagged for correction",
  rejected: "Rejected",
};

const nf = new Intl.NumberFormat("nb-NO");
const decimal = new Intl.NumberFormat("nb-NO", {
  maximumFractionDigits: 3,
});

function clamp(value, min = 0, max = 1) {
  return Math.min(max, Math.max(min, value));
}

function normalize(value) {
  return String(value ?? "")
    .normalize("NFC")
    .toLocaleLowerCase("nb-NO");
}

function padPage(value) {
  return String(value ?? "").padStart(4, "0");
}

function morphologyText(alternatives) {
  if (!Array.isArray(alternatives) || alternatives.length === 0) return "—";
  return alternatives.map((tags) => tags.join(" · ")).join("  /  ");
}

function getPairFrequencyMetric(pair, frequencyMode = "available") {
  const multi =
    pair?.multi_corpus_frequency?.pair_profiles?.[frequencyMode];

  if (multi && multi.status !== "pending") {
    const leftProfile =
      pair?.multi_corpus_frequency?.accent_1?.profiles?.[frequencyMode];
    const rightProfile =
      pair?.multi_corpus_frequency?.accent_2?.profiles?.[frequencyMode];

    const leftPpm = leftProfile?.best_frequency_per_million;
    const rightPpm = rightProfile?.best_frequency_per_million;
    const pairPpm =
      Number.isFinite(leftPpm) && Number.isFinite(rightPpm)
        ? multi.basis === "shared_written_form"
          ? leftPpm
          : Math.min(leftPpm, rightPpm)
        : Number.isFinite(leftPpm)
        ? leftPpm
        : Number.isFinite(rightPpm)
        ? rightPpm
        : null;

    return {
      score: Number(multi.ranking_value ?? 0),
      ppm: pairPpm,
      basis: multi.basis,
      count: null,
      tier: pairFrequencyTier(pair, frequencyMode),
      status: multi.status,
      scope: frequencyMode,
    };
  }

  // Backward-compatible fallback: the old corpus represents modern use.
  if (frequencyMode === "historical") return null;
  if (!hasCurrentDisplayFrequency(pair, "modern")) return null;

  const evidence = pair?.frequency_evidence?.pair;
  if (!evidence) return null;

  if (evidence.written_forms_identical_casefold) {
    const ppm = evidence.shared_surface_frequency_per_million;
    return Number.isFinite(ppm)
      ? {
          score: ppm,
          ppm,
          basis: "shared_written_form",
          count: evidence.shared_surface_token_count,
          tier: evidence.shared_surface_frequency_tier,
          status: "both_attested",
          scope: frequencyMode,
        }
      : null;
  }

  const ppm = evidence.rarer_written_side_frequency_per_million;
  return Number.isFinite(ppm)
    ? {
        score: ppm,
        ppm,
        basis: "rarer_written_side",
        count: evidence.rarer_written_side_count,
        tier: evidence.pedagogical_surface_frequency_tier,
        status: "both_attested",
        scope: frequencyMode,
      }
    : null;
}

function selectedForm(side) {
  return (
    side?.form_current ??
    side?.form_selected ??
    side?.form_corrected ??
    side?.form_ocr ??
    null
  );
}

function applyPatches(pair, manualCorrections, reviewOverrides) {
  const manual = manualCorrections?.[pair.id] ?? null;
  const persistedReview = pair?.research_review ?? null;
  const review = reviewOverrides?.[pair.id] ?? persistedReview;

  return {
    ...pair,
    accent_1: {
      ...pair.accent_1,
      form_display:
        review?.accent_1_form ??
        manual?.accent_1_form ??
        selectedForm(pair.accent_1),
    },
    accent_2: {
      ...pair.accent_2,
      form_display:
        review?.accent_2_form ??
        manual?.accent_2_form ??
        selectedForm(pair.accent_2),
    },
    manual_correction: manual,
    research_review: review,
  };
}

function getDisplayForms(pair) {
  return {
    accent1:
      pair?.accent_1?.form_display ??
      selectedForm(pair?.accent_1) ??
      "—",
    accent2:
      pair?.accent_2?.form_display ??
      selectedForm(pair?.accent_2) ??
      "—",
  };
}


function frequencyMatchesDisplayedForm(evidence, displayedForm) {
  if (!evidence || !displayedForm) return false;
  return normalize(evidence.surface_form) === normalize(displayedForm);
}

function getDisplayFrequencyEvidence(
  pair,
  accent,
  frequencyMode = "available"
) {
  const forms = getDisplayForms(pair);
  const displayedForm = accent === 1 ? forms.accent1 : forms.accent2;
  const sideName = accent === 1 ? "accent_1" : "accent_2";
  const multiSide = pair?.multi_corpus_frequency?.[sideName];
  const profile = multiSide?.profiles?.[frequencyMode];

  if (
    profile &&
    normalize(multiSide?.surface_form) === normalize(displayedForm)
  ) {
    const current =
      profile.status === "attested" || profile.status === "unattested";

    return {
      measurable: current,
      surface_form: displayedForm,
      token_count_casefold: profile.raw_count_sum_across_sources,
      frequency_per_million: profile.best_frequency_per_million,
      frequency_tier: profile.tier,
      display_frequency_status: current ? "current" : "pending",
      profile_scope: frequencyMode,
      profile,
      sources: Object.values(multiSide?.sources ?? {}).filter((source) => {
        if (frequencyMode === "available") return true;
        return (
          pair?.multi_corpus_frequency_catalog?.sources?.[source.source_id]
            ?.scope === frequencyMode
        );
      }),
      reason:
        profile.status === "pending"
          ? "One or more configured corpus lookups are still pending."
          : null,
    };
  }

  // Backward-compatible fallback for JSON without multi-corpus enrichment.
  if (frequencyMode === "historical") {
    return {
      measurable: null,
      surface_form: displayedForm,
      frequency_tier: "pending",
      display_frequency_status: "pending",
      profile_scope: frequencyMode,
      sources: [],
      reason: "Historical corpus evidence has not been added yet.",
    };
  }

  const evidence =
    accent === 1
      ? pair?.frequency_evidence?.accent_1_written_form
      : pair?.frequency_evidence?.accent_2_written_form;

  if (!evidence) {
    return {
      measurable: null,
      surface_form: displayedForm,
      frequency_tier: "pending_recalculation",
      display_frequency_status: "missing",
      profile_scope: frequencyMode,
      sources: [],
      reason: "No frequency evidence is stored for the displayed form.",
    };
  }

  if (!frequencyMatchesDisplayedForm(evidence, displayedForm)) {
    return {
      measurable: null,
      surface_form: displayedForm,
      frequency_tier: "pending_recalculation",
      display_frequency_status: "stale_surface_form",
      stale_evidence_surface_form: evidence.surface_form ?? null,
      profile_scope: frequencyMode,
      sources: [],
      reason:
        "The stored frequency belongs to a different spelling and cannot be displayed as the frequency of the current form.",
    };
  }

  if (
    evidence.frequency_tier === "pending_recalculation" ||
    evidence.measurable == null ||
    evidence.token_count_casefold == null ||
    evidence.frequency_per_million == null
  ) {
    return {
      ...evidence,
      measurable: null,
      surface_form: displayedForm,
      display_frequency_status: "pending",
      profile_scope: frequencyMode,
      sources: [],
      reason:
        evidence.reason ??
        "The selected spelling has not yet been looked up in the corpus.",
    };
  }

  return {
    ...evidence,
    display_frequency_status: "current",
    profile_scope: frequencyMode,
    sources: [],
  };
}

function hasCurrentDisplayFrequency(pair, frequencyMode = "available") {
  const a = getDisplayFrequencyEvidence(pair, 1, frequencyMode);
  const b = getDisplayFrequencyEvidence(pair, 2, frequencyMode);
  return (
    a?.display_frequency_status === "current" &&
    b?.display_frequency_status === "current"
  );
}

/**
 * "Text confidence" is a transparent review heuristic, not a calibrated
 * character-recognition probability.
 */
function getDisplayQuality(pair) {
  const review = pair?.research_review;
  const manual = pair?.manual_correction;

  if (review?.status === "verified" || manual?.verified_against_scan) {
    return {
      score: 1,
      level: "green",
      reasons: ["Verified manually against the printed scan."],
      source: "manual",
    };
  }

  if (review?.status === "rejected") {
    return {
      score: 0,
      level: "red",
      reasons: ["Record was rejected during research review."],
      source: "manual",
    };
  }

  const stage2 = pair?.stage2_resolution;
  if (
    stage2?.replacement_made === true &&
    stage2?.verification_status === "automatic_replacement_unverified"
  ) {
    const candidateScore = Number(
      stage2?.ranked_alternatives?.[0]?.score ??
        stage2?.alignment_similarity_to_original_record ??
        0.72
    );

    return {
      score: clamp(candidateScore),
      level: "blue",
      reasons: [
        "The displayed form was selected from multi-scale OCR evidence.",
        "The replacement still requires comparison with the printed scan.",
      ],
      source: "stage2_ai_selection",
    };
  }

  let score = Number(pair?.structural_parse_assessment?.score ?? 0.5);
  const reasons = [];

  const parseFlags = pair?.structural_parse_assessment?.flags ?? [];
  if (parseFlags.length > 0) {
    score -= Math.min(0.28, parseFlags.length * 0.07);
    reasons.push(...parseFlags.map((flag) => `Parser flag: ${flag}`));
  }

  if (review?.status === "needs_correction") {
    score = Math.min(score, 0.65);
    reasons.unshift("Flagged for correction during research review.");
  }

  if (review?.status === "corrected") {
    score = Math.min(Math.max(score, 0.58), 0.76);
    reasons.unshift("A correction was entered but has not been verified.");
  }

  if (pair?.lexical_verification?.verified_against_scan !== true) {
    score -= 0.03;
  }

  score = clamp(score);
  const level = score >= 0.8 ? "green" : score >= 0.55 ? "yellow" : "red";

  if (reasons.length === 0) {
    reasons.push("No major structural anomaly was detected.");
  }

  return { score, level, reasons, source: "heuristic" };
}

function getCorpusStatus(pair, frequencyMode = "available") {
  const a = getDisplayFrequencyEvidence(pair, 1, frequencyMode);
  const b = getDisplayFrequencyEvidence(pair, 2, frequencyMode);
  const pairProfile =
    pair?.multi_corpus_frequency?.pair_profiles?.[frequencyMode];
  const tier = pairFrequencyTier(pair, frequencyMode);
  const tierLabel = frequencyTierLabel(tier);

  if (
    a?.display_frequency_status !== "current" ||
    b?.display_frequency_status !== "current"
  ) {
    const label = `${FREQUENCY_MODE_LABELS[frequencyMode]} pending`;
    return {
      level: "pending",
      primaryLabel: label,
      tierLabel: null,
      short: label,
      long:
        "The selected spelling has not yet been checked in every configured source for this frequency view.",
      tier: null,
    };
  }

  if (pairProfile?.status === "both_attested") {
    const availabilityText =
      frequencyMode === "available"
        ? "Both forms available"
        : `Both forms: ${FREQUENCY_MODE_LABELS[frequencyMode]}`;

    return {
      level: "measured",
      primaryLabel: availabilityText,
      tierLabel: tierLabel ?? null,
      short: tierLabel
        ? `${availabilityText} · ${tierLabel}`
        : availabilityText,
      long: tierLabel
        ? `Both displayed forms are attested. The pair tier is ${tierLabel.toLocaleLowerCase(
            "en-US"
          )}, based on the rarer of the two written forms in this view.`
        : "Both displayed forms are attested in at least one source belonging to this view.",
      tier,
    };
  }

  if (pairProfile?.status === "one_side_attested") {
    return {
      level: "neutral",
      primaryLabel: "One side attested",
      tierLabel: tierLabel ?? null,
      short: tierLabel
        ? `One side attested · ${tierLabel}`
        : "One side attested",
      long: tierLabel
        ? `Only one displayed side is attested. Its tier is ${tierLabel.toLocaleLowerCase(
            "en-US"
          )}.`
        : "Only one displayed side is attested in the selected modern/historical view.",
      tier,
    };
  }

  if (pairProfile?.status === "neither_attested") {
    return {
      level: "neutral",
      primaryLabel: "No matches in this view",
      tierLabel: "Unattested",
      short: "No matches in this view",
      long:
        "Neither displayed form was found in the configured sources for this view. This is not automatically a spelling error.",
      tier: "unattested",
    };
  }

  const aZero = Number(a.token_count_casefold ?? 0) === 0;
  const bZero = Number(b.token_count_casefold ?? 0) === 0;

  if (aZero && bZero) {
    return {
      level: "neutral",
      primaryLabel: "0 corpus matches",
      tierLabel: "Unattested",
      short: "0 corpus matches",
      long:
        "Both displayed forms were absent from the selected corpus evidence.",
      tier: "unattested",
    };
  }

  if (aZero || bZero) {
    return {
      level: "neutral",
      primaryLabel: "One side: 0 matches",
      tierLabel: tierLabel ?? null,
      short: tierLabel
        ? `One side: 0 matches · ${tierLabel}`
        : "One side: 0 corpus matches",
      long:
        "One displayed form was absent from the selected corpus evidence. The tier describes the attested side.",
      tier,
    };
  }

  const metric = getPairFrequencyMetric(pair, frequencyMode);
  const metricTierLabel = frequencyTierLabel(metric?.tier);

  return {
    level: metric ? "measured" : "neutral",
    primaryLabel: "Frequency available",
    tierLabel: metricTierLabel ?? null,
    short: metricTierLabel
      ? `Frequency available · ${metricTierLabel}`
      : "Frequency available",
    long:
      "Written-form frequency is shown separately for each configured source.",
    tier: metric?.tier ?? null,
  };
}

function buildRanks(pairs, frequencyMode = "available") {
  const measurable = pairs
    .map((pair) => ({ pair, metric: getPairFrequencyMetric(pair, frequencyMode) }))
    .filter(
      ({ metric, pair }) =>
        metric &&
        !pair?.manual_correction?.frequency_is_stale &&
        !pair?.research_review?.frequency_is_stale &&
        pair?.research_review?.status !== "rejected"
    )
    .sort((a, b) => b.metric.score - a.metric.score);

  const global = new Map();
  measurable.forEach(({ pair, metric }, index) => {
    global.set(pair.id, {
      rank: index + 1,
      total: measurable.length,
      percentile:
        measurable.length > 1
          ? 100 * (1 - index / (measurable.length - 1))
          : 100,
      metric,
    });
  });

  const byGroup = new Map();
  const buckets = new Map();

  measurable.forEach((entry) => {
    const groupId = entry.pair.group_id;
    if (!buckets.has(groupId)) buckets.set(groupId, []);
    buckets.get(groupId).push(entry);
  });

  buckets.forEach((entries, groupId) => {
    const groupMap = new Map();
    entries.forEach(({ pair, metric }, index) => {
      groupMap.set(pair.id, {
        rank: index + 1,
        total: entries.length,
        metric,
      });
    });
    byGroup.set(groupId, groupMap);
  });

  return { global, byGroup };
}

function SearchIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <circle cx="11" cy="11" r="7" />
      <path d="m20 20-3.5-3.5" />
    </svg>
  );
}

function CloseIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M6 6l12 12M18 6 6 18" />
    </svg>
  );
}

function ChevronIcon({ open }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function ArrowIcon({ direction }) {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className={`h-4 w-4 ${direction === "right" ? "rotate-180" : ""}`}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="m15 18-6-6 6-6" />
    </svg>
  );
}

function ExternalIcon() {
  return (
    <svg
      aria-hidden="true"
      viewBox="0 0 24 24"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
    >
      <path d="M14 5h5v5M19 5l-9 9" />
      <path d="M19 13v6H5V5h6" />
    </svg>
  );
}

function QualityLegend() {
  return (
    <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-slate-600">
      {Object.entries(QUALITY_STYLES).map(([key, style]) => (
        <span key={key} className="inline-flex items-center gap-1.5">
          <span className={`h-2.5 w-2.5 rounded-full ${style.dot}`} />
          {style.label}
        </span>
      ))}
      <span className="text-slate-400">
        Text confidence reflects structural parsing and review status; corpus frequency is displayed separately.
      </span>
    </div>
  );
}

function CorpusBadge({ pair, frequencyMode }) {
  const status = getCorpusStatus(pair, frequencyMode);

  const styles = {
    measured: "border-indigo-200 bg-indigo-50 text-indigo-900",
    warning: "border-amber-200 bg-amber-50 text-amber-950",
    pending: "border-sky-200 bg-sky-50 text-sky-900",
    neutral: "border-slate-200 bg-slate-50 text-slate-700",
  };

  const tierStyle = tierBadgeStyle(status.tier);
  const tierMarker = tierBadgeMarker(status.tier);

  return (
    <span
      title={status.long}
      className={`inline-flex min-h-8 flex-wrap items-center gap-1.5 rounded-xl border px-2 py-1 text-[11px] leading-tight shadow-sm ${styles[status.level]}`}
    >
      <span className="font-semibold tracking-tight">
        {status.primaryLabel ?? status.short}
      </span>
      {status.tierLabel ? (
        <span
          className={`inline-flex min-w-[5.75rem] items-center justify-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.09em] ${tierStyle}`}
        >
          <span aria-hidden="true" className="text-[9px] leading-none">
            {tierMarker}
          </span>
          {status.tierLabel}
        </span>
      ) : null}
    </span>
  );
}

function VerificationBadge({ pair }) {
  const status = pair?.research_review?.status;
  const manualVerified = pair?.manual_correction?.verified_against_scan;

  if (manualVerified || status === "verified") {
    return (
      <span className="rounded-full border border-emerald-200 bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-800">
        Scan verified
      </span>
    );
  }

  if (status === "corrected") {
    return (
      <span className="rounded-full border border-sky-200 bg-sky-50 px-2 py-0.5 text-[10px] font-semibold text-sky-800">
        Correction entered
      </span>
    );
  }

  if (status === "needs_correction") {
    return (
      <span className="rounded-full border border-amber-200 bg-amber-50 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
        Flagged
      </span>
    );
  }

  if (status === "rejected") {
    return (
      <span className="rounded-full border border-rose-200 bg-rose-50 px-2 py-0.5 text-[10px] font-semibold text-rose-800">
        Rejected
      </span>
    );
  }

  return null;
}

function PairRow({ pair, selected, onSelect, frequencyMode }) {
  const forms = getDisplayForms(pair);
  const quality = getDisplayQuality(pair);
  const style = QUALITY_STYLES[quality.level];

  return (
    <button
      type="button"
      onClick={() => onSelect(pair)}
      aria-pressed={selected}
      className={[
        "group mb-0.5 w-full break-inside-avoid border-b border-l-4 border-b-stone-200",
        "px-3 py-2.5 text-left transition focus:outline-none focus:ring-2",
        "focus:ring-slate-500 focus:ring-inset",
        style.border,
        style.row,
        selected ? "bg-slate-100 ring-2 ring-slate-700 ring-inset" : "",
      ].join(" ")}
    >
      <div className="flex items-start gap-2.5">
        <span
          className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${style.dot}`}
          title={`${style.label}: ${Math.round(quality.score * 100)}%`}
        />

        <div className="min-w-0 flex-1">
          <div className="font-serif text-[15px] leading-5 text-slate-950">
            <span className="font-medium">{forms.accent1}</span>
            <span className="px-1.5 text-stone-400">/</span>
            <span>{forms.accent2}</span>
          </div>

          <div className="mt-1 flex min-h-5 flex-wrap items-center gap-1.5">
            <span className="text-[10px] font-medium uppercase tracking-wide text-stone-500">
              Text {Math.round(quality.score * 100)}%
            </span>
            <VerificationBadge pair={pair} />
            <CorpusBadge pair={pair} frequencyMode={frequencyMode} />
          </div>
        </div>

        <span className="mt-1 text-xs text-stone-300 opacity-0 transition group-hover:opacity-100">
          Open
        </span>
      </div>
    </button>
  );
}

function Stat({ label, value, helper }) {
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
        {label}
      </div>
      <div className="mt-1 text-lg font-semibold text-slate-950">{value}</div>
      {helper ? <div className="mt-1 text-xs leading-5 text-slate-500">{helper}</div> : null}
    </div>
  );
}

function FrequencySide({ title, evidence }) {
  const pending =
    evidence?.display_frequency_status === "pending" ||
    evidence?.display_frequency_status === "missing" ||
    evidence?.display_frequency_status === "stale_surface_form" ||
    evidence?.measurable == null ||
    evidence?.token_count_casefold == null ||
    evidence?.frequency_per_million == null;

  if (pending) {
    return (
      <div className="rounded-lg border border-sky-200 bg-sky-50 p-3">
        <div className="text-xs font-semibold text-sky-900">{title}</div>
        <div className="mt-1 font-serif text-lg text-slate-950">
          {evidence?.surface_form ?? "—"}
        </div>
        <div className="mt-2 text-sm font-semibold text-sky-900">
          Frequency pending
        </div>
        <div className="mt-1 text-xs leading-5 text-sky-800">
          {evidence?.reason ??
            "The selected spelling has not yet been looked up in the corpus."}
        </div>
        {evidence?.stale_evidence_surface_form ? (
          <div className="mt-1 text-[11px] text-sky-700">
            Obsolete frequency form: {evidence.stale_evidence_surface_form}
          </div>
        ) : null}
      </div>
    );
  }

  if (evidence?.measurable === false) {
    return (
      <div className="rounded-lg border border-slate-200 bg-white p-3">
        <div className="text-xs font-semibold text-slate-700">{title}</div>
        <div className="mt-1 font-serif text-lg text-slate-950">
          {evidence?.surface_form ?? "—"}
        </div>
        <div className="mt-2 text-sm text-slate-500">Not measurable</div>
        {evidence?.reason ? (
          <div className="mt-1 text-xs text-slate-400">{evidence.reason}</div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="rounded-lg border border-slate-200 bg-white p-3">
      <div className="text-xs font-semibold text-slate-700">{title}</div>
      <div className="mt-1 font-serif text-lg text-slate-950">
        {evidence.surface_form}
      </div>
      <dl className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
        <dt className="text-slate-500">Tokens</dt>
        <dd className="text-right font-medium text-slate-800">
          {nf.format(evidence.token_count_casefold)}
        </dd>
        <dt className="text-slate-500">Per million</dt>
        <dd className="text-right font-medium text-slate-800">
          {decimal.format(evidence.frequency_per_million)}
        </dd>
        <dt className="text-slate-500">Tier</dt>
        <dd className="text-right font-medium text-slate-800">
          {FREQUENCY_TIER_LABELS[evidence.frequency_tier] ??
            evidence.frequency_tier ??
            "—"}
        </dd>
      </dl>

      {Array.isArray(evidence?.sources) && evidence.sources.length > 0 ? (
        <div className="mt-3 border-t border-slate-100 pt-2">
          <div className="text-[10px] font-semibold uppercase tracking-wide text-slate-400">
            Sources
          </div>
          <div className="mt-1 space-y-1">
            {evidence.sources.map((source) => (
              <div
                key={source.source_id}
                className="flex items-start justify-between gap-3 text-[11px]"
              >
                <span className="min-w-0 truncate text-slate-500">
                  {source.source_id}
                </span>
                <span className="shrink-0 font-medium text-slate-700">
                  {source.status === "pending"
                    ? "pending"
                    : `${nf.format(source.count ?? 0)}${
                        Number.isFinite(source.frequency_per_million)
                          ? ` · ${decimal.format(
                              source.frequency_per_million
                            )} ppm`
                          : ""
                      }`}
                </span>
              </div>
            ))}
          </div>
        </div>
      ) : null}
    </div>
  );
}

function SourceComparison({
  pair,
  scanImageUrl,
  sourcePageUrl,
}) {
  const forms = getDisplayForms(pair);
  const raw = pair?.source?.raw_ocr;
  const differsFromRaw =
    normalize(raw).replace(/\s+/g, " ") !==
    normalize(`${forms.accent1} / ${forms.accent2}`).replace(/\s+/g, " ");

  return (
    <section className="rounded-xl border border-stone-300 bg-[#fbfaf6]">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-stone-300 px-4 py-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-950">Compare with the book</h3>
          <p className="mt-0.5 text-xs text-stone-500">
            Viewer page {pair?.source?.viewer_page ?? "—"} · printed page{" "}
            {pair?.source?.printed_page_estimate ?? "—"}
          </p>
        </div>

        {sourcePageUrl ? (
          <a
            href={sourcePageUrl}
            target="_blank"
            rel="noreferrer"
            className="inline-flex items-center gap-1.5 rounded-md border border-stone-300 bg-white px-3 py-1.5 text-xs font-semibold text-slate-700 hover:border-slate-400 hover:bg-stone-50"
          >
            Open source page
            <ExternalIcon />
          </a>
        ) : null}
      </div>

      {scanImageUrl ? (
        <div className="border-b border-stone-300 bg-stone-100 p-3">
          <div className="max-h-[420px] overflow-auto rounded border border-stone-300 bg-white">
            <img
              src={scanImageUrl}
              alt={`Scan of viewer page ${pair?.source?.viewer_page ?? ""}`}
              className="mx-auto max-w-none"
              loading="lazy"
            />
          </div>
        </div>
      ) : (
        <div className="border-b border-stone-300 bg-stone-50 px-4 py-3 text-xs text-stone-500">
          Add <code className="rounded bg-white px-1">scanImageBasePath</code> or{" "}
          <code className="rounded bg-white px-1">getScanImageUrl</code> to show
          the scanned page here.
        </div>
      )}

      <div className="grid gap-3 p-4 sm:grid-cols-2">
        <div className="rounded-lg border border-stone-200 bg-white p-3">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            Raw OCR
          </div>
          <div className="mt-1 font-serif text-base text-slate-900">{raw ?? "—"}</div>
        </div>

        <div
          className={[
            "rounded-lg border p-3",
            differsFromRaw
              ? "border-sky-200 bg-sky-50"
              : "border-stone-200 bg-white",
          ].join(" ")}
        >
          <div className="text-[11px] font-semibold uppercase tracking-wide text-stone-500">
            Current database form
          </div>
          <div className="mt-1 font-serif text-base text-slate-950">
            {forms.accent1} / {forms.accent2}
          </div>
          {differsFromRaw ? (
            <div className="mt-1 text-xs text-sky-800">Edited from the OCR form.</div>
          ) : null}
        </div>
      </div>
    </section>
  );
}

function ReviewActions({
  pair,
  onApplyReview,
}) {
  const forms = getDisplayForms(pair);
  const [editing, setEditing] = useState(false);
  const [accent1, setAccent1] = useState(forms.accent1);
  const [accent2, setAccent2] = useState(forms.accent2);
  const [note, setNote] = useState(pair?.research_review?.note ?? "");

  useEffect(() => {
    setEditing(false);
    setAccent1(forms.accent1);
    setAccent2(forms.accent2);
    setNote(pair?.research_review?.note ?? "");
  }, [pair.id, forms.accent1, forms.accent2, pair?.research_review?.note]);

  function commit(status, additional = {}) {
    onApplyReview(pair.id, {
      ...pair?.research_review,
      status,
      note,
      reviewed_at: new Date().toISOString(),
      ...additional,
    });
  }

  return (
    <section className="rounded-xl border border-slate-300 bg-white p-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h3 className="text-sm font-semibold text-slate-950">Research decision</h3>
          <p className="mt-0.5 text-xs text-slate-500">
            Decisions are stored locally unless an <code>onReviewChange</code> callback persists them.
          </p>
        </div>

        {pair?.research_review?.status ? (
          <span className="rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-xs font-semibold text-slate-700">
            {REVIEW_STATUS_LABELS[pair.research_review.status] ??
              pair.research_review.status}
          </span>
        ) : null}
      </div>

      {editing ? (
        <div className="mt-4 rounded-lg border border-sky-200 bg-sky-50 p-3">
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="text-xs font-semibold text-slate-700">
              Accent 1 form
              <input
                value={accent1}
                onChange={(event) => setAccent1(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-serif text-base font-normal outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              />
            </label>
            <label className="text-xs font-semibold text-slate-700">
              Accent 2 form
              <input
                value={accent2}
                onChange={(event) => setAccent2(event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 font-serif text-base font-normal outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              />
            </label>
          </div>

          <label className="mt-3 block text-xs font-semibold text-slate-700">
            Review note
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={3}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-sky-500 focus:ring-2 focus:ring-sky-200"
              placeholder="Describe what was corrected and why."
            />
          </label>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() =>
                commit("corrected", {
                  accent_1_form: accent1.trim(),
                  accent_2_form: accent2.trim(),
                  frequency_is_stale:
                    normalize(accent1.trim()) !==
                      normalize(
                        pair?.frequency_evidence?.accent_1_written_form
                          ?.surface_form
                      ) ||
                    normalize(accent2.trim()) !==
                      normalize(
                        pair?.frequency_evidence?.accent_2_written_form
                          ?.surface_form
                      ),
                })
              }
              className="rounded-md bg-sky-700 px-3 py-2 text-xs font-semibold text-white hover:bg-sky-800"
            >
              Save correction
            </button>
            <button
              type="button"
              onClick={() => setEditing(false)}
              className="rounded-md border border-slate-300 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50"
            >
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <>
          <label className="mt-4 block text-xs font-semibold text-slate-700">
            Review note
            <textarea
              value={note}
              onChange={(event) => setNote(event.target.value)}
              rows={2}
              className="mt-1 w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm font-normal outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
              placeholder="Optional note about the scan, spelling or grouping."
            />
          </label>

          <div className="mt-3 flex flex-wrap gap-2">
            <button
              type="button"
              onClick={() => commit("verified")}
              className="rounded-md bg-emerald-700 px-3 py-2 text-xs font-semibold text-white hover:bg-emerald-800"
            >
              Mark verified
            </button>
            <button
              type="button"
              onClick={() => setEditing(true)}
              className="rounded-md border border-sky-300 bg-sky-50 px-3 py-2 text-xs font-semibold text-sky-900 hover:bg-sky-100"
            >
              Correct spelling
            </button>
            <button
              type="button"
              onClick={() => commit("needs_correction")}
              className="rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-950 hover:bg-amber-100"
            >
              Flag for correction
            </button>
            <button
              type="button"
              onClick={() => commit("rejected")}
              className="rounded-md border border-rose-300 bg-rose-50 px-3 py-2 text-xs font-semibold text-rose-900 hover:bg-rose-100"
            >
              Reject record
            </button>
          </div>
        </>
      )}
    </section>
  );
}

function PairDetailsDrawer({
  pair,
  group,
  globalRank,
  groupRank,
  scanImageUrl,
  sourcePageUrl,
  previousPair,
  nextPair,
  nextReviewPair,
  onNavigate,
  onClose,
  onApplyReview,
  frequencyMode,
  detailLoading = false,
  detailError = null,
  detailLoaded = true,
}) {
  useEffect(() => {
    if (!pair) return undefined;

    function handleKeyDown(event) {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowLeft" && previousPair) onNavigate(previousPair);
      if (event.key === "ArrowRight" && nextPair) onNavigate(nextPair);
      if (event.key.toLocaleLowerCase() === "n" && nextReviewPair) {
        onNavigate(nextReviewPair);
      }
    }

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [
    pair,
    previousPair,
    nextPair,
    nextReviewPair,
    onClose,
    onNavigate,
  ]);

  if (!pair) return null;

  const forms = getDisplayForms(pair);
  const quality = getDisplayQuality(pair);
  const qualityStyle = QUALITY_STYLES[quality.level];
  const evidence = pair?.frequency_evidence;
  const accent1DisplayFrequency = getDisplayFrequencyEvidence(pair, 1, frequencyMode);
  const accent2DisplayFrequency = getDisplayFrequencyEvidence(pair, 2, frequencyMode);
  const pairEvidence = hasCurrentDisplayFrequency(pair, frequencyMode)
    ? evidence?.pair
    : null;
  const corpusStatus = getCorpusStatus(pair, frequencyMode);
  const disambiguation =
    DISAMBIGUATION_LABELS[pairEvidence?.disambiguation_class] ??
    pairEvidence?.disambiguation_class ??
    "No disambiguation information.";
  const frequencyMetric = getPairFrequencyMetric(pair, frequencyMode);
  const structuralScore = pair?.structural_parse_assessment?.score;

  return (
    <div className="fixed inset-0 z-50 flex justify-end">
      <button
        type="button"
        aria-label="Close pair details"
        className="absolute inset-0 bg-slate-950/55 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      <aside
        role="dialog"
        aria-modal="true"
        aria-labelledby="pair-details-title"
        className="relative h-full w-full max-w-3xl overflow-y-auto border-l border-slate-300 bg-slate-50 shadow-2xl ring-1 ring-slate-900/10"
      >
        <div className="sticky top-0 z-20 border-b border-slate-200 bg-white shadow-sm">
          <div className="flex items-center justify-between gap-3 px-5 py-3">
            <div className="flex min-w-0 items-center gap-2">
              <button
                type="button"
                disabled={!previousPair}
                onClick={() => previousPair && onNavigate(previousPair)}
                className="rounded-md border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                title="Previous visible pair (←)"
              >
                <ArrowIcon direction="left" />
              </button>
              <button
                type="button"
                disabled={!nextPair}
                onClick={() => nextPair && onNavigate(nextPair)}
                className="rounded-md border border-slate-200 p-2 text-slate-500 hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-30"
                title="Next visible pair (→)"
              >
                <ArrowIcon direction="right" />
              </button>

              <div className="min-w-0 pl-1">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-slate-500">
                  Group {pair.group_id} · record {pair.id}
                </div>
                <h2
                  id="pair-details-title"
                  className="truncate font-serif text-xl font-semibold text-slate-950"
                >
                  {forms.accent1} / {forms.accent2}
                </h2>
              </div>
            </div>

            <div className="flex items-center gap-2">
              {nextReviewPair ? (
                <button
                  type="button"
                  onClick={() => onNavigate(nextReviewPair)}
                  className="hidden rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-xs font-semibold text-amber-950 hover:bg-amber-100 sm:inline-flex"
                  title="Next item requiring review (N)"
                >
                  Next review item
                </button>
              ) : null}

              <button
                type="button"
                onClick={onClose}
                className="rounded-md p-2 text-slate-500 hover:bg-slate-100 hover:text-slate-950"
              >
                <CloseIcon />
              </button>
            </div>
          </div>
        </div>

        <div className="space-y-5 p-5">
          {detailLoading ? (
            <section
              className="rounded-xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950 shadow-sm"
              role="status"
            >
              <div className="font-bold">Loading complete record…</div>
              <p className="mt-1 text-xs leading-5 text-sky-800">
                The pair and frequency summary are already visible. OCR
                evidence, morphology and full corpus details are being loaded.
              </p>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-white">
                <div className="h-full w-2/5 animate-pulse rounded-full bg-sky-600" />
              </div>
            </section>
          ) : null}

          {detailError ? (
            <section
              className="rounded-xl border border-rose-200 bg-rose-50 p-4 text-sm text-rose-950 shadow-sm"
              role="alert"
            >
              <div className="font-bold">Could not load complete record</div>
              <p className="mt-1 break-words text-xs leading-5">
                {detailError}
              </p>
            </section>
          ) : null}

          {!detailLoading && !detailError && !detailLoaded ? (
            <section className="rounded-xl border border-slate-200 bg-white p-4 text-sm text-slate-600">
              Open the record again to load its complete evidence.
            </section>
          ) : null}

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wider text-slate-500">
                  Tonelag contrast
                </div>
                <div className="mt-3 grid grid-cols-[1fr_auto_1fr] items-center gap-3">
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Accent 1
                    </div>
                    <div className="mt-1 font-serif text-2xl text-slate-950">
                      {forms.accent1}
                    </div>
                  </div>
                  <div className="text-xl text-slate-300">↔</div>
                  <div className="rounded-lg bg-slate-50 p-3 text-center">
                    <div className="text-[11px] font-semibold uppercase tracking-wide text-slate-500">
                      Accent 2
                    </div>
                    <div className="mt-1 font-serif text-2xl text-slate-950">
                      {forms.accent2}
                    </div>
                  </div>
                </div>
              </div>

              <span
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${qualityStyle.badge}`}
              >
                {qualityStyle.label}
              </span>
            </div>

            <div className="mt-4">
              <div className="flex justify-between text-xs text-slate-500">
                <span>Text confidence</span>
                <span>{Math.round(quality.score * 100)}%</span>
              </div>
              <div className="mt-1 h-2 overflow-hidden rounded-full bg-slate-100">
                <div
                  className={`h-full rounded-full ${qualityStyle.bar}`}
                  style={{ width: `${quality.score * 100}%` }}
                />
              </div>
              <ul className="mt-2 space-y-1 text-xs text-slate-500">
                {quality.reasons.map((reason) => (
                  <li key={reason}>• {reason}</li>
                ))}
              </ul>
              {Number.isFinite(structuralScore) ? (
                <p className="mt-2 text-[11px] leading-5 text-slate-400">
                  Structural parse score: {Math.round(structuralScore * 100)}%.
                  Text confidence is a review heuristic, not a calibrated OCR probability.
                </p>
              ) : null}
            </div>
          </section>

          <SourceComparison
            pair={pair}
            scanImageUrl={scanImageUrl}
            sourcePageUrl={sourcePageUrl}
          />

          <ReviewActions pair={pair} onApplyReview={onApplyReview} />

          <section>
            <h3 className="text-sm font-semibold text-slate-950">
              Corpus evidence · {FREQUENCY_MODE_LABELS[frequencyMode]}
            </h3>

            <div
              className={[
                "mt-2 rounded-lg border p-3 text-sm",
                corpusStatus.level === "warning"
                  ? "border-amber-200 bg-amber-50 text-amber-950"
                  : corpusStatus.level === "pending"
                  ? "border-sky-200 bg-sky-50 text-sky-950"
                  : corpusStatus.level === "neutral"
                  ? "border-slate-200 bg-slate-50 text-slate-800"
                  : "border-indigo-100 bg-indigo-50 text-indigo-950",
              ].join(" ")}
            >
              <div className="flex flex-wrap items-center gap-2">
                <div className="font-semibold">
                  {corpusStatus.primaryLabel ?? corpusStatus.short}
                </div>
                {corpusStatus.tierLabel ? (
                  <span
                    className={`inline-flex min-w-[6.25rem] items-center justify-center gap-1 rounded-full border px-2.5 py-1 text-[10px] font-black uppercase tracking-[0.09em] ${
                      tierBadgeStyle(corpusStatus.tier)
                    }`}
                  >
                    <span aria-hidden="true" className="text-[9px] leading-none">
                      {tierBadgeMarker(corpusStatus.tier)}
                    </span>
                    {corpusStatus.tierLabel}
                  </span>
                ) : null}
              </div>
              <p className="mt-1 text-xs leading-5">{corpusStatus.long}</p>
            </div>

            <div className="mt-2 grid grid-cols-2 gap-2">
              <Stat
                label={`${FREQUENCY_MODE_LABELS[frequencyMode]} rank`}
                value={
                  corpusStatus.level === "pending"
                    ? "Refresh needed"
                    : globalRank
                    ? `#${globalRank.rank}`
                    : "—"
                }
                helper={
                  globalRank
                    ? `of ${nf.format(globalRank.total)} measurable records`
                    : "Not a meaning-specific tonelag rank"
                }
              />
              <Stat
                label="Rank in group"
                value={
                  corpusStatus.level === "pending"
                    ? "Refresh needed"
                    : groupRank
                    ? `#${groupRank.rank}`
                    : "—"
                }
                helper={
                  groupRank
                    ? `of ${nf.format(groupRank.total)} measurable records`
                    : `Group ${pair.group_id}`
                }
              />
              <Stat
                label="Pair metric"
                value={
                  corpusStatus.level === "pending"
                    ? "—"
                    : frequencyMetric
                    ? Number.isFinite(frequencyMetric.ppm)
                      ? `${decimal.format(frequencyMetric.ppm)} ppm`
                      : "Attested"
                    : "—"
                }
                helper={
                  frequencyMetric?.basis === "shared_written_form"
                    ? "Shared written-form frequency"
                    : frequencyMetric?.basis === "rarer_written_side"
                    ? "Rarer written side"
                    : "No pair-level metric"
                }
              />
              <Stat
                label="Surface tier"
                value={
                  corpusStatus.level === "pending"
                    ? "Pending"
                    : FREQUENCY_TIER_LABELS[frequencyMetric?.tier] ??
                      frequencyMetric?.tier ??
                      "—"
                }
                helper={
                  pairEvidence?.written_form_balance_ratio != null
                    ? `Balance ratio ${decimal.format(
                        pairEvidence.written_form_balance_ratio
                      )}`
                    : "Balance unavailable for shared spelling"
                }
              />
            </div>

            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <FrequencySide
                title="Accent 1 written form"
                evidence={accent1DisplayFrequency}
              />
              <FrequencySide
                title="Accent 2 written form"
                evidence={accent2DisplayFrequency}
              />
            </div>

            <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-xs leading-5 text-slate-600">
              <div className="font-semibold text-slate-800">Interpretation</div>
              <p className="mt-1">{disambiguation}</p>
              {pairEvidence?.warning ? <p className="mt-1">{pairEvidence.warning}</p> : null}
            </div>
          </section>

          <section>
            <h3 className="text-sm font-semibold text-slate-950">
              Morphology and classification
            </h3>
            <div className="mt-2 grid grid-cols-1 gap-2 sm:grid-cols-2">
              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold text-slate-500">
                  Accent 1
                </div>
                <div className="mt-1 text-sm text-slate-800">
                  {morphologyText(
                    pair?.accent_1?.default_morphology_alternatives
                  )}
                </div>
                {pair?.accent_1?.inline_annotation_ocr ? (
                  <div className="mt-2 text-xs text-slate-500">
                    OCR annotation: {pair.accent_1.inline_annotation_ocr}
                  </div>
                ) : null}
              </div>

              <div className="rounded-lg border border-slate-200 bg-white p-3">
                <div className="text-xs font-semibold text-slate-500">
                  Accent 2
                </div>
                <div className="mt-1 text-sm text-slate-800">
                  {morphologyText(
                    pair?.accent_2?.default_morphology_alternatives
                  )}
                </div>
                {pair?.accent_2?.inline_annotation_ocr ? (
                  <div className="mt-2 text-xs text-slate-500">
                    OCR annotation: {pair.accent_2.inline_annotation_ocr}
                  </div>
                ) : null}
              </div>
            </div>

            <div className="mt-2 rounded-lg border border-slate-200 bg-white p-3 text-sm text-slate-700">
              <div className="font-semibold text-slate-900">
                Group {pair.group_id}
              </div>
              <p className="mt-1">{group?.description ?? "No group description."}</p>
              <p className="mt-1 text-xs text-slate-500">
                Orthography relation: {pair?.orthography_relation ?? "—"} ·
                segmental equivalence claimed:{" "}
                {pair?.claimed_segmental_equivalence ? "yes" : "no"}
              </p>
            </div>
          </section>

          <section className="rounded-xl border border-slate-200 bg-white p-4">
            <h3 className="text-sm font-semibold text-slate-950">
              Record provenance
            </h3>
            <dl className="mt-3 grid grid-cols-[auto_1fr] gap-x-4 gap-y-2 text-sm">
              <dt className="text-slate-500">Record ID</dt>
              <dd className="font-mono text-xs text-slate-800">{pair.id}</dd>
              <dt className="text-slate-500">Viewer page</dt>
              <dd className="text-slate-800">
                {pair?.source?.viewer_page ?? "—"}
              </dd>
              <dt className="text-slate-500">Printed page</dt>
              <dd className="text-slate-800">
                {pair?.source?.printed_page_estimate ?? "—"}
              </dd>
              <dt className="text-slate-500">OCR line</dt>
              <dd className="text-slate-800">
                {pair?.source?.ocr_line ?? "—"}
              </dd>
              <dt className="text-slate-500">Separator</dt>
              <dd className="font-mono text-slate-800">
                {pair?.source?.separator_ocr ?? "—"}
              </dd>
              <dt className="text-slate-500">Scan verified</dt>
              <dd className="text-slate-800">
                {pair?.manual_correction?.verified_against_scan ||
                pair?.research_review?.status === "verified" ||
                pair?.lexical_verification?.verified_against_scan
                  ? "Yes"
                  : "No"}
              </dd>
            </dl>
          </section>

          <p className="pb-3 text-center text-[11px] text-slate-400">
            Keyboard: ← previous · → next · N next review item · Esc close
          </p>
        </div>
      </aside>
    </div>
  );
}

function GroupNavButton({
  group,
  selected,
  onSelect,
  visibleCount,
  reviewCount,
}) {
  return (
    <button
      type="button"
      aria-current={selected ? "page" : undefined}
      onClick={() => onSelect(group.id)}
      style={
        selected
          ? {
              color: "#ffffff",
              backgroundColor: "#0f172a",
              borderColor: "#0f172a",
            }
          : undefined
      }
      className={[
        "relative flex w-full items-start justify-between gap-3 rounded-md border px-3 py-2 text-left",
        "transition outline-none focus-visible:ring-2 focus-visible:ring-slate-700 focus-visible:ring-offset-1",
        selected
          ? "!border-slate-900 !bg-slate-900 !text-white shadow-sm"
          : "border-transparent text-slate-700 hover:border-stone-300 hover:bg-stone-100",
      ].join(" ")}
    >
      {selected ? (
        <span
          aria-hidden="true"
          className="absolute inset-y-1 left-0 w-1 rounded-r bg-amber-400"
        />
      ) : null}

      <span
        className={[
          "min-w-0 pl-1",
          selected ? "!text-white" : "text-slate-700",
        ].join(" ")}
      >
        <span
          className={[
            "font-semibold",
            selected ? "!text-white" : "",
          ].join(" ")}
        >
          {group.id}.
        </span>{" "}
        <span
          className={[
            "font-serif",
            selected ? "!text-white" : "",
          ].join(" ")}
        >
          {group.example}
        </span>
      </span>

      <span className="flex shrink-0 items-center gap-1.5">
        {reviewCount > 0 ? (
          <span
            title={`${reviewCount} records require review`}
            className="h-2 w-2 rounded-full bg-amber-400"
          />
        ) : null}
        <span
          className={[
            "text-[11px] tabular-nums",
            selected ? "!text-slate-200" : "text-stone-500",
          ].join(" ")}
        >
          {visibleCount ?? group.extracted_pair_candidates ?? group.reported_count}
        </span>
      </span>
    </button>
  );
}

function FilterButton({ active, onClick, children, tone = "neutral" }) {
  const activeStyles = {
    neutral: "border-slate-900 bg-slate-900 text-white",
    blue: "border-sky-700 bg-sky-700 text-white",
    green: "border-emerald-700 bg-emerald-700 text-white",
    yellow: "border-amber-500 bg-amber-400 text-amber-950",
    red: "border-rose-700 bg-rose-700 text-white",
  };

  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "rounded-full border px-3 py-1.5 text-xs font-semibold transition",
        active
          ? activeStyles[tone]
          : "border-stone-300 bg-white text-slate-700 hover:border-slate-400 hover:bg-stone-50",
      ].join(" ")}
    >
      {children}
    </button>
  );
}

export default function TonelagGroupsExplorer({
  data,
  initialGroupId = 1,
  initialMode = "browse",
  manualCorrections = DEFAULT_MANUAL_CORRECTIONS,
  title = "Bokmålets tonelagspar",
  scanImageBasePath = null,
  getScanImageUrl = null,
  getSourcePageUrl = null,
  onReviewChange = null,
  enableMongoTransfer = false,
  mongoApiBaseUrl = TONELAG_API_BASE_URL,
  authToken = null,
  mongoDatasetId = "bokmaalets-tonelagspar",
  mongoRequestHeaders = null,
  mongoBatchSize = 25,
  mongoMaxBatchBytes = 1_500_000,
  recordDetailsById = null,
  recordDetailsLoadingById = null,
  recordDetailsErrorsById = null,
  onRequestRecordDetail = null,
}) {
  const groups = data?.classification?.groups ?? [];
  const endingClasses = data?.classification?.ending_classes ?? [];
  const rawPairs = data?.pair_candidates ?? [];

  const [reviewOverrides, setReviewOverrides] = useState({});
  const [selectedGroupId, setSelectedGroupId] = useState(initialGroupId);
  const [selectedPairId, setSelectedPairId] = useState(null);
  const [query, setQuery] = useState("");
  const [qualityFilter, setQualityFilter] = useState("all");
  const [sortMode, setSortMode] = useState("book");
  const [frequencyMode, setFrequencyMode] = useState("available");
  const [viewMode, setViewMode] = useState(initialMode);
  const [mongoTransfer, setMongoTransfer] = useState({
    state: "idle",
    completed: 0,
    total: 0,
    message: "",
  });
  const [mobileGroupsOpen, setMobileGroupsOpen] = useState(false);
  const [openEndingClasses, setOpenEndingClasses] = useState(() =>
    Object.fromEntries(endingClasses.map((endingClass) => [endingClass.id, true]))
  );

  const pairs = useMemo(
    () =>
      rawPairs.map((pair, bookIndex) => ({
        ...applyPatches(pair, manualCorrections, reviewOverrides),
        _bookIndex: bookIndex,
      })),
    [rawPairs, manualCorrections, reviewOverrides]
  );

  const groupsById = useMemo(
    () => new Map(groups.map((group) => [group.id, group])),
    [groups]
  );

  const pairsByGroup = useMemo(() => {
    const map = new Map();
    groups.forEach((group) => map.set(group.id, []));

    pairs.forEach((pair) => {
      if (!map.has(pair.group_id)) map.set(pair.group_id, []);
      map.get(pair.group_id).push(pair);
    });

    return map;
  }, [groups, pairs]);

  const ranks = useMemo(
    () => buildRanks(pairs, frequencyMode),
    [pairs, frequencyMode]
  );

  useEffect(() => {
    if (!groupsById.has(selectedGroupId) && groups.length > 0) {
      setSelectedGroupId(groups[0].id);
    }
  }, [groups, groupsById, selectedGroupId]);

  useEffect(() => {
    const selectedGroup = groupsById.get(selectedGroupId);
    if (!selectedGroup) return;

    setOpenEndingClasses((current) => ({
      ...current,
      [selectedGroup.ending_class]: true,
    }));
  }, [groupsById, selectedGroupId]);

  const selectedGroup = groupsById.get(selectedGroupId) ?? groups[0] ?? null;

  function isReviewItem(pair) {
    const level = getDisplayQuality(pair).level;
    return (
      level !== "green" ||
      pair?.research_review?.status === "needs_correction" ||
      pair?.research_review?.status === "corrected"
    );
  }

  function matchesPair(pair) {
    const quality = getDisplayQuality(pair);

    if (viewMode === "review" && !isReviewItem(pair)) return false;
    if (qualityFilter !== "all" && quality.level !== qualityFilter) return false;

    const normalizedQuery = normalize(query).trim();
    if (!normalizedQuery) return true;

    const forms = getDisplayForms(pair);
    const haystack = normalize(
      [
        forms.accent1,
        forms.accent2,
        pair.id,
        pair.group_id,
        pair?.source?.raw_ocr,
        pair?.source?.viewer_page,
        pair?.source?.printed_page_estimate,
        pair?.frequency_evidence?.pair?.pedagogical_surface_frequency_tier,
        pair?.research_review?.note,
      ].join(" ")
    );

    return haystack.includes(normalizedQuery);
  }

  const filteredAllPairs = useMemo(
    () => pairs.filter(matchesPair),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairs, query, qualityFilter, viewMode]
  );

  const visiblePairs = useMemo(() => {
    const source = query.trim()
      ? filteredAllPairs
      : (pairsByGroup.get(selectedGroupId) ?? []).filter(matchesPair);

    return [...source].sort((a, b) => {
      if (viewMode === "review" || sortMode === "confidence") {
        return (
          getDisplayQuality(a).score - getDisplayQuality(b).score ||
          a._bookIndex - b._bookIndex
        );
      }

      if (sortMode === "frequency") {
        const af =
          getPairFrequencyMetric(a, frequencyMode)?.score ?? -1;
        const bf =
          getPairFrequencyMetric(b, frequencyMode)?.score ?? -1;
        return bf - af || a._bookIndex - b._bookIndex;
      }

      return a._bookIndex - b._bookIndex;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filteredAllPairs,
    pairsByGroup,
    selectedGroupId,
    query,
    qualityFilter,
    sortMode,
    viewMode,
    frequencyMode,
  ]);

  const selectedPairSummary =
    pairs.find((pair) => pair.id === selectedPairId) ?? null;

  const selectedPairDetailRaw =
    selectedPairId && recordDetailsById
      ? recordDetailsById[selectedPairId] ?? null
      : null;

  const selectedPair = useMemo(() => {
    if (!selectedPairSummary) return null;
    if (!selectedPairDetailRaw) return selectedPairSummary;

    return {
      ...applyPatches(
        selectedPairDetailRaw,
        manualCorrections,
        reviewOverrides
      ),
      _bookIndex: selectedPairSummary._bookIndex,
    };
  }, [
    selectedPairSummary,
    selectedPairDetailRaw,
    manualCorrections,
    reviewOverrides,
  ]);

  const selectedPairDetailLoading =
    Boolean(
      selectedPairId &&
        recordDetailsLoadingById?.[selectedPairId]
    );

  const selectedPairDetailError =
    selectedPairId
      ? recordDetailsErrorsById?.[selectedPairId] ?? null
      : null;

  const selectedPairDetailLoaded =
    !onRequestRecordDetail ||
    Boolean(selectedPairId && selectedPairDetailRaw);

  function openPair(pair) {
    if (!pair) return;

    setSelectedPairId(pair.id);
    setSelectedGroupId(pair.group_id);
    onRequestRecordDetail?.(pair.id);
  }

  const selectedGlobalRank = selectedPair
    ? ranks.global.get(selectedPair.id)
    : null;

  const selectedGroupRank = selectedPair
    ? ranks.byGroup.get(selectedPair.group_id)?.get(selectedPair.id)
    : null;

  const selectedVisibleIndex = selectedPair
    ? visiblePairs.findIndex((pair) => pair.id === selectedPair.id)
    : -1;

  const previousPair =
    selectedVisibleIndex > 0 ? visiblePairs[selectedVisibleIndex - 1] : null;

  const nextPair =
    selectedVisibleIndex >= 0 && selectedVisibleIndex < visiblePairs.length - 1
      ? visiblePairs[selectedVisibleIndex + 1]
      : null;

  const allReviewPairs = useMemo(
    () =>
      [...pairs]
        .filter(isReviewItem)
        .sort(
          (a, b) =>
            getDisplayQuality(a).score - getDisplayQuality(b).score ||
            a._bookIndex - b._bookIndex
        ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [pairs]
  );

  const nextReviewPair = selectedPair
    ? allReviewPairs.find(
        (pair) =>
          pair._bookIndex > selectedPair._bookIndex &&
          pair.id !== selectedPair.id
      ) ?? allReviewPairs.find((pair) => pair.id !== selectedPair.id)
    : null;

  const qualityCounts = useMemo(() => {
    const counts = { blue: 0, green: 0, yellow: 0, red: 0 };
    const base = query.trim()
      ? pairs.filter((pair) => {
          if (viewMode === "review" && !isReviewItem(pair)) return false;
          const normalizedQuery = normalize(query).trim();
          if (!normalizedQuery) return true;
          const forms = getDisplayForms(pair);
          return normalize(
            [
              forms.accent1,
              forms.accent2,
              pair.id,
              pair.group_id,
              pair?.source?.raw_ocr,
            ].join(" ")
          ).includes(normalizedQuery);
        })
      : pairsByGroup.get(selectedGroupId) ?? [];

    base.forEach((pair) => {
      counts[getDisplayQuality(pair).level] += 1;
    });
    return counts;
  }, [pairs, pairsByGroup, query, selectedGroupId, viewMode]);

  const visibleCountsByGroup = useMemo(() => {
    const result = new Map();

    groups.forEach((group) => {
      const count = (pairsByGroup.get(group.id) ?? []).filter(matchesPair).length;
      result.set(group.id, count);
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, pairsByGroup, query, qualityFilter, viewMode]);

  const reviewCountsByGroup = useMemo(() => {
    const result = new Map();

    groups.forEach((group) => {
      const count = (pairsByGroup.get(group.id) ?? []).filter(isReviewItem).length;
      result.set(group.id, count);
    });

    return result;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [groups, pairsByGroup]);

  function applyReview(pairId, patch) {
    setReviewOverrides((current) => {
      const next = {
        ...current,
        [pairId]: {
          ...current[pairId],
          ...patch,
        },
      };

      onReviewChange?.({
        pairId,
        review: next[pairId],
        allReviews: next,
      });

      return next;
    });
  }

  function normalizedMongoApiBaseUrl() {
    return normalizeTonelagApiBase(mongoApiBaseUrl);
  }

  function mongoHeaders() {
    return buildTonelagHeaders({
      authToken,
      json: true,
      extraHeaders: mongoRequestHeaders,
    });
  }

  async function readJsonResponse(response) {
    let payload = null;

    try {
      payload = await response.json();
    } catch {
      payload = null;
    }

    if (!response.ok) {
      const detail =
        payload?.detail ??
        payload?.message ??
        `${response.status} ${response.statusText}`;

      const readableDetail =
        typeof detail === "string"
          ? detail
          : JSON.stringify(detail, null, 2);

      throw new Error(
        `${response.status} ${response.statusText}: ${readableDetail}`
      );
    }

    return payload;
  }

  function stringifyImportPayload(payload, context) {
    try {
      return JSON.stringify(payload, (_key, value) => {
        if (typeof value === "bigint") {
          return value.toString();
        }

        if (
          typeof value === "number" &&
          !Number.isFinite(value)
        ) {
          return null;
        }

        return value;
      });
    } catch (error) {
      throw new Error(
        `Could not serialize ${context}: ${
          error instanceof Error ? error.message : String(error)
        }`
      );
    }
  }

  function utf8ByteLength(value) {
    return new TextEncoder().encode(value).length;
  }

  function prepareMongoBatches(sourceRecords) {
    const maxRecords = Math.max(
      1,
      Math.min(250, Number(mongoBatchSize) || 25)
    );
    const maxBytes = Math.max(
      100_000,
      Number(mongoMaxBatchBytes) || 1_500_000
    );

    const batches = [];
    let current = [];
    let currentStart = 0;

    sourceRecords.forEach((record, bookIndex) => {
      const item = {
        book_index: bookIndex,
        record,
      };

      const trial = [...current, item];
      const trialBody = stringifyImportPayload(
        {
          dataset_id: mongoDatasetId,
          records: trial,
        },
        `records ${currentStart + 1}–${bookIndex + 1}`
      );
      const trialBytes = utf8ByteLength(trialBody);

      if (
        current.length > 0 &&
        (trial.length > maxRecords || trialBytes > maxBytes)
      ) {
        const body = stringifyImportPayload(
          {
            dataset_id: mongoDatasetId,
            records: current,
          },
          `records ${currentStart + 1}–${bookIndex}`
        );

        batches.push({
          start: currentStart,
          end: bookIndex,
          records: current,
          body,
          bytes: utf8ByteLength(body),
        });

        current = [item];
        currentStart = bookIndex;
      } else {
        current = trial;
      }
    });

    if (current.length > 0) {
      const body = stringifyImportPayload(
        {
          dataset_id: mongoDatasetId,
          records: current,
        },
        `records ${currentStart + 1}–${sourceRecords.length}`
      );

      batches.push({
        start: currentStart,
        end: sourceRecords.length,
        records: current,
        body,
        bytes: utf8ByteLength(body),
      });
    }

    return batches;
  }

  async function transferDatasetToMongo() {
    if (!data || !Array.isArray(data.pair_candidates)) return;
    if (mongoTransfer.state === "running") return;

    const total = data.pair_candidates.length;
    const confirmed = window.confirm(
      `Transfer ${total.toLocaleString()} records to MongoDB?\n\n` +
        `Dataset: ${mongoDatasetId}\n` +
        "The existing MongoDB copy of this dataset will be replaced."
    );
    if (!confirmed) return;

    const api = normalizedMongoApiBaseUrl();
    const metadata = { ...data };
    delete metadata.pair_candidates;

    let preparedBatches;

    try {
      preparedBatches = prepareMongoBatches(data.pair_candidates);
    } catch (error) {
      setMongoTransfer({
        state: "error",
        completed: 0,
        total,
        message:
          error instanceof Error
            ? error.message
            : "Could not prepare MongoDB batches.",
      });
      return;
    }

    const largestBatchBytes = Math.max(
      0,
      ...preparedBatches.map((batch) => batch.bytes)
    );

    console.info("Tonelag MongoDB import plan", {
      records: total,
      batches: preparedBatches.length,
      largestBatchBytes,
      api,
    });

    setMongoTransfer({
      state: "running",
      completed: 0,
      total,
      message: "Starting MongoDB import…",
    });

    try {
      const startResponse = await fetch(`${api}/imports/start`, {
        method: "POST",
        headers: mongoHeaders(),
        body: stringifyImportPayload(
          {
            dataset_id: mongoDatasetId,
            metadata,
            expected_records: total,
            replace_existing: true,
          },
          "dataset metadata"
        ),
      });
      await readJsonResponse(startResponse);

      for (
        let batchIndex = 0;
        batchIndex < preparedBatches.length;
        batchIndex += 1
      ) {
        const batch = preparedBatches[batchIndex];
        const rangeLabel =
          `${batch.start + 1}–${batch.end}`;

        setMongoTransfer({
          state: "running",
          completed: batch.start,
          total,
          message:
            `Sending records ${rangeLabel} ` +
            `(${(batch.bytes / 1_000_000).toFixed(2)} MB, ` +
            `batch ${batchIndex + 1}/${preparedBatches.length})…`,
        });

        const endpoint = `${api}/imports/batch`;

        let batchResponse;
        try {
          batchResponse = await fetch(endpoint, {
            method: "POST",
            headers: mongoHeaders(),
            body: batch.body,
          });
        } catch (error) {
          console.error("Tonelag batch request failed", {
            endpoint,
            batchIndex,
            rangeLabel,
            bytes: batch.bytes,
            error,
          });

          throw new Error(
            `Network failure while sending records ${rangeLabel} ` +
              `(${(batch.bytes / 1_000_000).toFixed(2)} MB) to ` +
              `${endpoint}: ${
                error instanceof Error
                  ? error.message
                  : String(error)
              }`
          );
        }

        await readJsonResponse(batchResponse);

        setMongoTransfer({
          state: "running",
          completed: batch.end,
          total,
          message:
            `Transferred ${batch.end.toLocaleString()} of ` +
            `${total.toLocaleString()} records…`,
        });
      }

      const finishResponse = await fetch(`${api}/imports/finish`, {
        method: "POST",
        headers: mongoHeaders(),
        body: stringifyImportPayload(
          {
            dataset_id: mongoDatasetId,
          },
          "import completion request"
        ),
      });
      const finished = await readJsonResponse(finishResponse);

      if (!finished.complete) {
        throw new Error(
          `MongoDB contains ${finished.imported_records} of ` +
            `${finished.expected_records} expected records.`
        );
      }

      setMongoTransfer({
        state: "success",
        completed: total,
        total,
        message: `MongoDB transfer complete: ${total.toLocaleString()} records.`,
      });
    } catch (error) {
      setMongoTransfer((current) => ({
        state: "error",
        completed: current.completed,
        total,
        message:
          error instanceof Error
            ? error.message
            : "MongoDB transfer failed.",
      }));
    }
  }

  function resolveScanImageUrl(pair) {
    if (!pair) return null;
    if (getScanImageUrl) return getScanImageUrl(pair);
    if (!scanImageBasePath || !pair?.source?.viewer_page) return null;

    return `${scanImageBasePath.replace(/\/$/, "")}/page_${padPage(
      pair.source.viewer_page
    )}.jpg`;
  }

  function resolveSourcePageUrl(pair) {
    if (!pair) return null;
    if (getSourcePageUrl) return getSourcePageUrl(pair);

    const itemId = data?.source?.nb_item_id;
    const page = pair?.source?.viewer_page;
    if (!itemId || !page) return null;

    return `https://www.nb.no/items/${itemId}?page=${page}`;
  }

  function toggleEndingClass(id) {
    setOpenEndingClasses((current) => ({
      ...current,
      [id]: !current[id],
    }));
  }

  function selectGroup(groupId) {
    setSelectedGroupId(groupId);
    setQuery("");
    setMobileGroupsOpen(false);
  }

  if (!data || groups.length === 0) {
    return (
      <div className="rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-950">
        <div className="font-semibold">No tonelag dataset loaded</div>
        <p className="mt-1 text-sm">
          Pass the enriched JSON object through the component&apos;s{" "}
          <code className="rounded bg-white/70 px-1">data</code> prop.
        </p>
      </div>
    );
  }

  return (
    <div
      data-tonelag-ui-version="stage2-mongodb-lazy-details-v12"
      className="min-h-screen bg-[#f3f0e7] text-slate-900"
    >
      <header className="border-b border-stone-300 bg-[#faf8f2]">
        <div className="mx-auto max-w-[1800px] px-4 py-5 sm:px-6 lg:px-8">
          <div className="text-xs font-semibold uppercase tracking-[0.22em] text-stone-500">
            Martin Kloster Jensen · 1958
          </div>
          <h1 className="mt-1 font-serif text-3xl font-semibold tracking-tight text-slate-950">
            {title}
          </h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-600">
            Research database for comparing the OCR-derived inventory with the
            printed book. Two tonelag are organised into 21 grammatical and
            phonological pair-structure groups.
          </p>
          <QualityLegend />
        </div>
      </header>

      <div className="mx-auto grid max-w-[1800px] grid-cols-1 lg:grid-cols-[320px_minmax(0,1fr)]">
        <aside className="border-b border-stone-300 bg-[#faf8f2] lg:min-h-[calc(100vh-150px)] lg:border-b-0 lg:border-r">
          <div className="sticky top-0 max-h-screen overflow-y-auto p-4 sm:p-5">
            <button
              type="button"
              onClick={() => setMobileGroupsOpen((value) => !value)}
              className="flex w-full items-center justify-between rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm font-semibold lg:hidden"
            >
              Browse 21 groups
              <ChevronIcon open={mobileGroupsOpen} />
            </button>

            <nav
              aria-label="Tonelag pair-structure groups"
              className={`${mobileGroupsOpen ? "block" : "hidden"} mt-3 space-y-3 lg:block`}
            >
              {endingClasses.map((endingClass) => {
                const open = openEndingClasses[endingClass.id] !== false;
                const classReviewCount = (endingClass.groups ?? []).reduce(
                  (sum, groupId) => sum + (reviewCountsByGroup.get(groupId) ?? 0),
                  0
                );

                return (
                  <section
                    key={endingClass.id}
                    className="rounded-lg border border-stone-200 bg-white/50"
                  >
                    <button
                      type="button"
                      onClick={() => toggleEndingClass(endingClass.id)}
                      className="flex w-full items-center justify-between gap-3 px-3 py-3 text-left"
                    >
                      <span>
                        <span className="text-[11px] font-bold uppercase tracking-[0.16em] text-stone-600">
                          {ENDING_CLASS_LABELS[endingClass.id] ??
                            endingClass.label ??
                            endingClass.id}
                        </span>
                        <span className="ml-2 text-[11px] text-stone-400">
                          {endingClass.reported_share_percent}%
                        </span>
                      </span>

                      <span className="flex items-center gap-2">
                        {classReviewCount > 0 ? (
                          <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-900">
                            {classReviewCount} review
                          </span>
                        ) : null}
                        <ChevronIcon open={open} />
                      </span>
                    </button>

                    {open ? (
                      <div className="space-y-1 border-t border-stone-200 p-2">
                        {(endingClass.groups ?? [])
                          .map((groupId) => groupsById.get(groupId))
                          .filter(Boolean)
                          .map((group) => (
                            <GroupNavButton
                              key={group.id}
                              group={group}
                              selected={!query.trim() && selectedGroupId === group.id}
                              visibleCount={visibleCountsByGroup.get(group.id)}
                              reviewCount={reviewCountsByGroup.get(group.id) ?? 0}
                              onSelect={selectGroup}
                            />
                          ))}
                      </div>
                    ) : null}
                  </section>
                );
              })}
            </nav>
          </div>
        </aside>

        <main className="min-w-0 px-4 py-5 sm:px-6 lg:px-8">
          <div className="sticky top-0 z-20 -mx-4 -mt-5 border-b border-stone-300 bg-[#f3f0e7]/95 px-4 py-4 backdrop-blur sm:-mx-6 sm:px-6 lg:-mx-8 lg:px-8">
            <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
              <div className="flex min-w-0 flex-1 flex-col gap-2 sm:flex-row">
                <div className="relative min-w-0 flex-1 xl:max-w-xl">
                  <span className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-stone-400">
                    <SearchIcon />
                  </span>
                  <input
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Search forms, IDs, OCR text or page numbers…"
                    className="w-full rounded-lg border border-stone-300 bg-white py-2 pl-9 pr-3 text-sm outline-none transition placeholder:text-stone-400 focus:border-slate-500 focus:ring-2 focus:ring-slate-200"
                  />
                </div>

                <div className="inline-flex rounded-lg border border-stone-300 bg-white p-1">
                  <button
                    type="button"
                    onClick={() => setViewMode("browse")}
                    className={[
                      "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                      viewMode === "browse"
                        ? "bg-slate-900 text-white"
                        : "text-slate-600 hover:bg-stone-100",
                    ].join(" ")}
                  >
                    Browse
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setViewMode("review");
                      setQualityFilter("all");
                    }}
                    className={[
                      "rounded-md px-3 py-1.5 text-xs font-semibold transition",
                      viewMode === "review"
                        ? "bg-amber-400 text-amber-950"
                        : "text-slate-600 hover:bg-stone-100",
                    ].join(" ")}
                  >
                    Review queue · {allReviewPairs.length}
                  </button>
                </div>

                <select
                  value={sortMode}
                  onChange={(event) => setSortMode(event.target.value)}
                  disabled={viewMode === "review"}
                  className="rounded-lg border border-stone-300 bg-white px-3 py-2 text-sm outline-none focus:border-slate-500 focus:ring-2 focus:ring-slate-200 disabled:cursor-not-allowed disabled:bg-stone-100 disabled:text-stone-400"
                  aria-label="Sort pairs"
                >
                  <option value="book">Book order</option>
                  <option value="frequency">Surface frequency</option>
                  <option value="confidence">Text confidence</option>
                </select>

                <div
                  className="inline-flex rounded-lg border border-stone-300 bg-white p-1"
                  aria-label="Frequency period"
                >
                  {Object.entries(FREQUENCY_MODE_LABELS).map(
                    ([mode, label]) => (
                      <button
                        key={mode}
                        type="button"
                        onClick={() => setFrequencyMode(mode)}
                        className={[
                          "rounded-md px-2.5 py-1.5 text-xs font-semibold transition",
                          frequencyMode === mode
                            ? "bg-indigo-700 text-white"
                            : "text-slate-600 hover:bg-stone-100",
                        ].join(" ")}
                      >
                        {label}
                      </button>
                    )
                  )}
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                <FilterButton
                  active={qualityFilter === "all"}
                  onClick={() => setQualityFilter("all")}
                >
                  All
                </FilterButton>
                <FilterButton
                  tone="blue"
                  active={qualityFilter === "blue"}
                  onClick={() => setQualityFilter("blue")}
                >
                  AI selected {qualityCounts.blue}
                </FilterButton>
                <FilterButton
                  tone="green"
                  active={qualityFilter === "green"}
                  onClick={() => setQualityFilter("green")}
                >
                  Green {qualityCounts.green}
                </FilterButton>
                <FilterButton
                  tone="yellow"
                  active={qualityFilter === "yellow"}
                  onClick={() => setQualityFilter("yellow")}
                >
                  Yellow {qualityCounts.yellow}
                </FilterButton>
                <FilterButton
                  tone="red"
                  active={qualityFilter === "red"}
                  onClick={() => setQualityFilter("red")}
                >
                  Red {qualityCounts.red}
                </FilterButton>
                {enableMongoTransfer ? (
                  <button
                    type="button"
                    onClick={transferDatasetToMongo}
                    disabled={mongoTransfer.state === "running"}
                    className={[
                      "inline-flex min-h-8 items-center justify-center rounded-full border px-3 py-1.5 text-xs font-bold transition",
                      mongoTransfer.state === "success"
                        ? "border-emerald-700 bg-emerald-700 text-white"
                        : mongoTransfer.state === "error"
                        ? "border-rose-700 bg-rose-700 text-white"
                        : "border-slate-900 bg-slate-900 text-white hover:bg-slate-800",
                      mongoTransfer.state === "running"
                        ? "cursor-wait opacity-75"
                        : "",
                    ].join(" ")}
                    title={`Transfer the complete dataset to MongoDB as ${mongoDatasetId}`}
                  >
                    {mongoTransfer.state === "running"
                      ? `MongoDB ${mongoTransfer.completed}/${mongoTransfer.total}`
                      : mongoTransfer.state === "success"
                      ? "MongoDB transferred"
                      : "Transfer to MongoDB"}
                  </button>
                ) : null}
              </div>
            </div>
          </div>

          {enableMongoTransfer && mongoTransfer.state !== "idle" ? (
            <div
              className={[
                "mt-4 rounded-xl border px-4 py-3 text-sm shadow-sm",
                mongoTransfer.state === "success"
                  ? "border-emerald-200 bg-emerald-50 text-emerald-950"
                  : mongoTransfer.state === "error"
                  ? "border-rose-200 bg-rose-50 text-rose-950"
                  : "border-sky-200 bg-sky-50 text-sky-950",
              ].join(" ")}
              role={mongoTransfer.state === "error" ? "alert" : "status"}
            >
              <div className="font-semibold">{mongoTransfer.message}</div>
              {mongoTransfer.state === "running" && mongoTransfer.total > 0 ? (
                <div className="mt-2 h-2 overflow-hidden rounded-full bg-white/80">
                  <div
                    className="h-full rounded-full bg-sky-600 transition-all"
                    style={{
                      width: `${Math.round(
                        (100 * mongoTransfer.completed) /
                          mongoTransfer.total
                      )}%`,
                    }}
                  />
                </div>
              ) : null}
            </div>
          ) : null}

          <section className="mt-6 overflow-hidden rounded-2xl border border-stone-300 bg-[#fbfaf6] shadow-sm">
            <div className="border-b border-stone-300 px-5 py-5 sm:px-7">
              {query.trim() ? (
                <>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                    Search across all 21 groups
                  </div>
                  <h2 className="mt-1 font-serif text-2xl font-semibold text-slate-950">
                    {visiblePairs.length} matching records
                  </h2>
                </>
              ) : (
                <>
                  <div className="text-xs font-semibold uppercase tracking-[0.18em] text-stone-500">
                    {ENDING_CLASS_LABELS[selectedGroup?.ending_class] ??
                      selectedGroup?.ending_class}
                  </div>
                  <h2 className="mt-1 font-serif text-2xl font-semibold text-slate-950">
                    {selectedGroup?.id}. gruppe
                  </h2>
                  <p className="mt-1 font-serif text-base text-slate-700">
                    Eksempel: {selectedGroup?.example}
                  </p>
                  <p className="mt-2 max-w-4xl text-sm text-slate-600">
                    {selectedGroup?.description}
                  </p>

                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-slate-600">
                      Reported {nf.format(selectedGroup?.reported_count ?? 0)}
                    </span>
                    <span className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-slate-600">
                      Extracted{" "}
                      {nf.format(
                        selectedGroup?.extracted_pair_candidates ??
                          (pairsByGroup.get(selectedGroupId) ?? []).length
                      )}
                    </span>
                    <span className="rounded-full border border-stone-300 bg-white px-2.5 py-1 text-slate-600">
                      Share {selectedGroup?.reported_share_percent ?? "—"}%
                    </span>
                    <span className="rounded-full border border-amber-200 bg-amber-50 px-2.5 py-1 text-amber-900">
                      Review {reviewCountsByGroup.get(selectedGroupId) ?? 0}
                    </span>
                  </div>
                </>
              )}
            </div>

            <div className="px-4 py-4 sm:px-6">
              {viewMode === "review" ? (
                <div className="mb-4 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-950">
                  <span className="font-semibold">Review mode:</span> records are
                  ordered from lowest to highest text confidence. Open a record,
                  compare it with the scanned page, and mark it verified,
                  corrected or rejected.
                </div>
              ) : null}

              {visiblePairs.length === 0 ? (
                <div className="rounded-xl border border-dashed border-stone-300 px-6 py-14 text-center text-sm text-stone-500">
                  No records match the current search and filters.
                </div>
              ) : (
                <div className="columns-1 gap-x-5 sm:columns-2 xl:columns-3 2xl:columns-4">
                  {visiblePairs.map((pair) => (
                    <PairRow
                      key={pair.id}
                      pair={pair}
                      selected={selectedPairId === pair.id}
                      onSelect={openPair}
                      frequencyMode={frequencyMode}
                    />
                  ))}
                </div>
              )}
            </div>
          </section>

          <div className="mt-4 grid gap-3 text-xs leading-5 text-stone-500 lg:grid-cols-2">
            <p>
              Text confidence is a transparent review heuristic. It is not a
              calibrated probability that every letter is correct.
            </p>
            <p>
              Corpus frequency concerns written forms. Identical spellings may
              combine several meanings, grammatical analyses and tonelag.
            </p>
          </div>
        </main>
      </div>

      <PairDetailsDrawer
        pair={selectedPair}
        group={
          selectedPair ? groupsById.get(selectedPair.group_id) : selectedGroup
        }
        globalRank={selectedGlobalRank}
        groupRank={selectedGroupRank}
        scanImageUrl={resolveScanImageUrl(selectedPair)}
        sourcePageUrl={resolveSourcePageUrl(selectedPair)}
        previousPair={previousPair}
        nextPair={nextPair}
        nextReviewPair={nextReviewPair}
        onNavigate={openPair}
        onClose={() => setSelectedPairId(null)}
        onApplyReview={applyReview}
        frequencyMode={frequencyMode}
        detailLoading={selectedPairDetailLoading}
        detailError={selectedPairDetailError}
        detailLoaded={selectedPairDetailLoaded}
      />
    </div>
  );
}

export { DEFAULT_MANUAL_CORRECTIONS };
